import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import './index.css';
import App from './App';
import { store } from './store/store';
import { ThemeProvider } from './contexts/ThemeContext';
import { isStaleSessionError } from './services/api';
import './i18n'; // Initialize i18n

// The default axios instance is used by services/shopApi.js. Stamp the same
// session markers the `api` instance stamps so the 403 kick below can tell a
// genuine role mismatch from a response to a request issued by an older,
// already-replaced session.
axios.interceptors.request.use((config) => {
  config._sentWithToken = localStorage.getItem('token') || null;
  config._sentAt = Date.now();
  return config;
});

// Global axios interceptor for 403 Forbidden errors
// Only handles role mismatch (outdated token with wrong role), not application-level 403s
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const errorMsg = error.response?.data?.message || '';
      // Only redirect on actual role/permission mismatch from auth middleware
      if (errorMsg === 'Insufficient permissions for this action' && !isStaleSessionError(error.config)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        const message = 'Your session is outdated. Please login again to refresh your permissions.';
        alert(message);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));

// StrictMode only in development to catch issues early
// In production, it's disabled to avoid double-rendering and redundant API calls
const AppWrapper = process.env.NODE_ENV === 'development' 
  ? ({ children }) => <React.StrictMode>{children}</React.StrictMode>
  : ({ children }) => <>{children}</>;

root.render(
  <AppWrapper>
    <HelmetProvider>
      <Provider store={store}>
        <ThemeProvider>
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <App />
        <Toaster 
          position="top-right"
          containerStyle={
            Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
              ? { top: 60 }
              : {}
          }
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              style: {
                background: '#10B981',
              },
            },
            error: {
              duration: 5000,
              style: {
                background: '#EF4444',
              },
            },
          }}
        />
      </BrowserRouter>
        </ThemeProvider>
    </Provider>
    </HelmetProvider>
  </AppWrapper>
);